"use client";

import { useEffect, useState } from "react";
import { toast } from "react-hot-toast";
import { extractApiErrorMessage } from "../../../utils/error-handler";
import { createRefund } from "../../../services/billingService";
import { formatPaise, rupeesToPaise } from "../../../utils/billingFormat";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

const RefundPaymentModal = ({ isOpen, onClose, onSaved, schoolId, payment, remainingRefundable }) => {
  const [amount, setAmount] = useState("");
  const [reason, setReason] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (isOpen && remainingRefundable != null) {
      setAmount(String(remainingRefundable / 100));
      setReason("");
    }
  }, [isOpen, remainingRefundable]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const paise = rupeesToPaise(amount);
    if (paise <= 0 || paise > remainingRefundable) {
      toast.error(`Amount must be between ₹0.01 and ${formatPaise(remainingRefundable)}`);
      return;
    }
    setSubmitting(true);
    try {
      await createRefund(schoolId, payment._id, { amount: paise, reason: reason.trim() });
      toast.success("Refund recorded");
      onSaved?.();
      onClose();
    } catch (error) {
      toast.error(extractApiErrorMessage(error));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Refund Payment</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <p className="text-sm text-gray-500">
            Remaining refundable: <span className="font-semibold text-gray-900">{formatPaise(remainingRefundable)}</span>
            {payment?.provider === "MANUAL" && " (manual payment — recorded only, no Razorpay refund call)"}
          </p>
          <div>
            <Label>Refund Amount (₹) *</Label>
            <Input type="number" min="0.01" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} required />
          </div>
          <div>
            <Label>Reason *</Label>
            <Textarea value={reason} onChange={(e) => setReason(e.target.value)} required placeholder="Why is this being refunded?" />
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" variant="destructive" disabled={submitting}>
              {submitting ? "Processing..." : "Refund"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default RefundPaymentModal;
